import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRunIntake } from "../../hooks/use-websites.js";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { IntakeSummary } from "../../components/intake-summary.js";
import { Button } from "../../components/button.js";
import type { WizardState, IntakeResult } from "../../api/types.js";

interface IntakeChecksStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function IntakeChecksStep({
  websiteId,
  wizardState,
}: IntakeChecksStepProps) {
  const qc = useQueryClient();
  const runIntake = useRunIntake(websiteId);
  const advance = useAdvanceWizardStep(websiteId);

  // Check for previously cached intake result
  const cachedResult = qc.getQueryData<IntakeResult>(["intake", websiteId]);

  // Auto-run intake if we haven't already
  useEffect(() => {
    if (!cachedResult && !runIntake.isPending && !runIntake.data) {
      runIntake.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = runIntake.data ?? cachedResult ?? null;

  function handleContinue() {
    if (!result) return;
    advance.mutate({
      step: "discovery_preview",
      outcome: result.hardFail
        ? "hard_fail"
        : result.warnings.length > 0
          ? "soft_warning"
          : result.advisories.length > 0
            ? "advisory"
            : "pass",
      data: {
        hardFail: result.hardFail,
        hardFailReason: result.hardFailReason,
        warningCount: result.warnings.length,
        advisoryCount: result.advisories.length,
      },
    });
  }

  function handleRerun() {
    runIntake.mutate();
  }

  // Check for wizard step data from a previous run
  const stepData = wizardState.steps.find(
    (s) => s.step === "discovery_preview",
  )?.data;
  const hadHardFail = stepData?.hardFail === true;

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Intake checks</h2>
      <p className="wizard-content__subtitle">
        We run a set of pre-scan checks to confirm your site is reachable and
        identify any conditions that may affect scanning.
      </p>

      {runIntake.isPending && (
        <div
          className="loading-text"
          style={{ textAlign: "left", padding: "1rem 0" }}
        >
          Running checks on your website…
        </div>
      )}

      {runIntake.error && (
        <div className="alert alert--error">
          {runIntake.error.message}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRerun}
            style={{ marginLeft: "1rem" }}
          >
            Retry
          </Button>
        </div>
      )}

      {result && <IntakeSummary result={result} />}

      {result && result.checks.wafDetected && (
        <div className="alert alert--warning" style={{ marginTop: "1rem" }}>
          <strong>{result.checks.wafDetected} is protecting this site</strong>
          <p style={{ margin: "0.5rem 0" }}>
            Your scan may return incomplete results unless you allowlist our
            scanner. We recommend setting this up before running your first scan.
          </p>
          <details style={{ marginTop: "0.5rem" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              How to allowlist in {result.checks.wafDetected}
            </summary>
            <ol style={{ marginTop: "0.5rem", paddingLeft: "1.25rem" }}>
              <li>
                Go to{" "}
                <strong>Security &rarr; WAF &rarr; Custom rules</strong> in
                your {result.checks.wafDetected} dashboard
              </li>
              <li>
                Create an exception rule: When <strong>User-Agent</strong>{" "}
                contains <code>AccessiMonitor</code> &rarr; Skip all managed
                rules
              </li>
              <li>
                Alternatively, set <strong>Security Level</strong> to
                &ldquo;Essentially Off&rdquo; for the scanner&rsquo;s IP range
                under Security &rarr; Settings
              </li>
            </ol>
            <p
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--color-text-muted)",
              }}
            >
              Our scanner identifies as:{" "}
              <code>Mozilla/5.0 (compatible; AccessiMonitor/1.0)</code>
            </p>
          </details>
          <p style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>
            After allowlisting, click <strong>Re-run checks</strong> below to
            verify.
          </p>
        </div>
      )}

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      <div className="wizard-content__actions">
        <Button
          onClick={handleContinue}
          loading={advance.isPending}
          disabled={!result || result.hardFail || runIntake.isPending}
        >
          Continue
        </Button>
        {result && (
          <Button
            variant="secondary"
            onClick={handleRerun}
            disabled={runIntake.isPending}
          >
            Re-run checks
          </Button>
        )}
      </div>

      {(result?.hardFail || hadHardFail) && (
        <div className="alert alert--error mt-3">
          Your site has a hard failure that must be resolved before you can
          proceed. Please fix the issue and re-run the checks.
        </div>
      )}
    </div>
  );
}
